// Release-cut result rendering — the per-repo PR table (status / author /
// reason) + the draft-PR link with a copy button. Shared by the run-detail
// page and the ticket-page lanes. Reads a `releaseCut` blob
// ({ repoId, branch, releasePrUrl, envReason, prs[] }).

import { useState } from "react";
import Icon from "./icons";

const STATUS_META = {
  merged: { icon: "✅", color: "var(--bss-success)", label: "merged" },
  conflict: { icon: "🔴", color: "var(--bss-danger)", label: "conflict" },
  skipped_red_ci: {
    icon: "⚪",
    color: "var(--bss-fg-muted)",
    label: "skipped (red CI)"
  },
  skipped_draft: {
    icon: "⚪",
    color: "var(--bss-fg-muted)",
    label: "skipped (draft)"
  },
  skipped_base: {
    icon: "⚪",
    color: "var(--bss-fg-muted)",
    label: "skipped (stacked)"
  }
};

function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  return (
    <button
      type="button"
      className="bss-btn bss-btn--ghost bss-btn--sm"
      onClick={() => {
        navigator.clipboard.writeText(text).then(
          () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          },
          () => {}
        );
      }}
    >
      <Icon.Copy size={12} /> {copied ? "Copied" : label || "Copy link"}
    </button>
  );
}

// `embedded` drops the outer card + header chrome — used inside a repo lane
// (which is already a card with its own header). Standalone (run-detail) keeps
// the full card.
export function ReleaseCutPanel({ releaseCut, embedded = false }) {
  if (!releaseCut || !Array.isArray(releaseCut.prs)) return null;
  const prs = releaseCut.prs;
  const counts = prs.reduce(
    (a, p) => {
      if (p.status === "merged") a.merged += 1;
      else if (p.status === "conflict") a.conflict += 1;
      else a.skipped += 1;
      return a;
    },
    { merged: 0, conflict: 0, skipped: 0 }
  );
  const table = (
    <>
      {releaseCut.envReason && (
        <div
          style={{
            padding: "8px 16px",
            color: "var(--bss-danger)",
            fontSize: 13,
            borderBottom: "1px solid var(--bss-border)"
          }}
        >
          ⚠️ {releaseCut.envReason}
        </div>
      )}
      <table className="bss-table">
        <thead>
          <tr>
            <th style={{ width: 30 }} aria-label="status" />
            <th>PR</th>
            <th style={{ width: 130 }}>Author</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {prs.map((p) => {
            const meta = STATUS_META[p.status] || {
              icon: "•",
              color: "var(--bss-fg-subtle)",
              label: p.status
            };
            return (
              <tr key={p.number}>
                <td title={meta.label} style={{ textAlign: "center" }}>
                  {meta.icon}
                </td>
                <td>
                  <span style={{ fontWeight: 600 }}>#{p.number}</span> {p.title}
                  {p.ticketKey && (
                    <span
                      style={{
                        color: "var(--bss-fg-subtle)",
                        marginLeft: 6,
                        fontSize: 12
                      }}
                    >
                      {p.ticketKey}
                    </span>
                  )}
                </td>
                <td style={{ color: "var(--bss-fg-muted)" }}>
                  {p.authorLogin ? `@${p.authorLogin}` : "—"}
                </td>
                <td style={{ color: meta.color, fontSize: 12.5 }}>
                  {p.reason || meta.label}
                  {Array.isArray(p.conflictFiles) &&
                    p.conflictFiles.length > 0 && (
                      <span style={{ color: "var(--bss-fg-subtle)" }}>
                        {" — "}
                        {p.conflictFiles.join(", ")}
                      </span>
                    )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );

  if (embedded) {
    return (
      <div style={{ borderTop: "1px solid var(--bss-border)" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 16px",
            color: "var(--bss-fg-subtle)",
            fontSize: 12
          }}
        >
          <span>
            {counts.merged} merged · {counts.conflict} conflict ·{" "}
            {counts.skipped} skipped
          </span>
          {releaseCut.releasePrUrl && (
            <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <a
                href={releaseCut.releasePrUrl}
                target="_blank"
                rel="noreferrer"
                className="bss-btn bss-btn--ghost bss-btn--sm"
              >
                <Icon.GitPullRequest size={12} /> Draft PR
              </a>
              <CopyButton text={releaseCut.releasePrUrl} />
            </span>
          )}
        </div>
        {table}
      </div>
    );
  }

  return (
    <div className="bss-card" style={{ overflow: "hidden", marginTop: 14 }}>
      <div className="bss-card-hd">
        <Icon.GitMerge size={14} />
        Release cut — {releaseCut.branch}
        <span
          style={{
            color: "var(--bss-fg-subtle)",
            marginLeft: 8,
            fontWeight: 400,
            fontSize: 12
          }}
        >
          {counts.merged} merged · {counts.conflict} conflict · {counts.skipped}{" "}
          skipped
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {releaseCut.releasePrUrl && (
            <>
              <a
                href={releaseCut.releasePrUrl}
                target="_blank"
                rel="noreferrer"
                className="bss-btn bss-btn--ghost bss-btn--sm"
              >
                <Icon.GitPullRequest size={12} /> Draft PR
              </a>
              <CopyButton text={releaseCut.releasePrUrl} />
            </>
          )}
        </div>
      </div>
      {table}
    </div>
  );
}
